import { useEffect, useRef, useState } from "react";
import AdminTopNavBar from "../common/AdminTopNavBar";
import getUserMenuItems from "./AdminMenuContent";
import AdminSideBar from "./AdminSideBar";
import { adminReqConfig, api } from "../../include/api";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useNavigate, useSearchParams } from "react-router-dom";
import AdminFooter from "../common/AdminFooter";
import { useNotification } from "../../Toast/ToastProvider";
import { ERROR, SUCCESS } from "../../include/constant";
import { ChevronDown, X } from "react-feather";
import Papa from "papaparse";
import "../../styles/CategoryProductList.css";
import { formatShortMonthDateYearWithTime, getAdmin } from "../../include/function";

const ListCustomer = () => {
  const navigate = useNavigate();
  const homeLink = "/admin";
  const [fetchLoading, setFetchLoading] = useState(true);
  const [customerList, setCustomerList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isSidebarMinimized, setIsSidebarMinimized] = useState(false);
  const [selectedData, setSelectedData] = useState([]);
  const toggleSidebar = () => setIsSidebarMinimized(!isSidebarMinimized);
  const menuContent = getUserMenuItems();
  const toast = useNotification();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [selectedCustomerType, setSelectedCustomerType] = useState("all");
  const user = getAdmin();
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const initialQuery = searchParams.get("q") || "";
  const [searchQuery, setSearchQuery] = useState(initialQuery);

  useEffect(() => {
    const q = searchParams.get("q") || "";
    setSearchQuery(q);
  }, [searchParams]);
  const [productPermissions, setProductPermissions] = useState({
    add: false,
    view: false,
    edit: false,
    delete: false,
  });

  // Only track customerType filter
  const [filterValues, setFilterValues] = useState({
    customerType: "",
  });

  const applyFilters = () => {
    // Only customerType matters now
    getCustomer({ customerType: filterValues.customerType });
    setIsFilterPanelOpen(false);
  };

  const clearFilters = () => {
    setFilterValues({ customerType: "" });
    getCustomer({ customerType: "" });
    setIsFilterPanelOpen(false);
  };

  useEffect(() => {
    const fetchPermissions = async () => {
      if (!user?.roleId) return;
      try {
        const res = await api.get(
          `/role_permission/?roleId=${user.roleId}`,
          adminReqConfig(),
        );
        if (res.status === 200 && Array.isArray(res.data.data)) {
          const productPerm = res.data.data.find(
            (p) => p.pageTitle === "Customers",
          );
          if (productPerm) {
            setProductPermissions({
              add: productPerm.add === "true" || productPerm.add === 1,
              view: productPerm.view === "true" || productPerm.view === 1,
              edit: productPerm.edit === "true" || productPerm.edit === 1,
              delete: productPerm.delete === "true" || productPerm.delete === 1,
            });
          }
        }
      } catch (err) {
        console.warn("Failed to fetch product permissions", err);
      }
    };
    fetchPermissions();
  }, [user?.roleId]);

  const handleStatusChange = (customerId, currentStatus) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    const payload = { status: newStatus };

    api
      .put(
        `/customer/?id=${customerId}&flag=changeStatus`,
        payload,
        adminReqConfig(),
      )
      .then((response) => {
        if (response.status === 200) {
          toast({
            type: SUCCESS,
            message: `Customer ${newStatus} successfully!`,
          });

          // Update customerList
          setCustomerList((prev) =>
            prev.map((c) =>
              c.id === customerId ? { ...c, status: newStatus } : c,
            ),
          );

          // Update filteredData (for immediate UI sync)
          setFilteredData((prev) =>
            prev.map((c) =>
              c.id === customerId ? { ...c, status: newStatus } : c,
            ),
          );
        }
      })
      .catch((error) => {
        console.error("Customer status update failed:", error);
        toast({ type: ERROR, message: "Failed to update customer status." });
      });
  };

  useEffect(() => {
    const leadsFilter = searchParams.get("leads");

    if (leadsFilter === "1") {
      setFetchLoading(true);
      api
        .get("/customer/?leads=1", adminReqConfig())
        .then((response) => {
          if (response.status === 200) {
            setCustomerList(response.data.data);
            setFilteredData(response.data.data);
          }
        })
        .catch((error) => {
          console.error("Failed to fetch new leads:", error);
          setCustomerList([]);
          setFilteredData([]);
        })
        .finally(() => setFetchLoading(false));
    } else {
      // Normal fetch (all customers)
      getCustomer();
    }
  }, [searchParams]); // 👈 Dependency on searchParams ensures it re-runs when URL changes

  const getCustomer = (filters = {}) => {
    setFetchLoading(true);
    api
      .get(`/customer/`, adminReqConfig())
      .then((response) => {
        if (response.status === 200) {
          let normalizedData = response.data.data;

          // Apply customerType filter locally
          if (filters.customerType) {
            normalizedData = normalizedData.filter(
              (c) =>
                c.customerType &&
                c.customerType.toLowerCase() ===
                  filters.customerType.toLowerCase(),
            );
          }

          setCustomerList(normalizedData);
          setFilteredData(normalizedData);
        }
      })
      .catch((error) => {
        console.error("Failed to fetch customers:", error);
        setCustomerList([]);
        setFilteredData([]);
      })
      .finally(() => setFetchLoading(false));
  };

  useEffect(() => {
    let filtered = customerList;
    if (searchQuery.trim()) {
      filtered = filtered.filter((customer) =>
        customer.name?.toLowerCase().startsWith(searchQuery.toLowerCase()),
      );
      setCurrentPage(1);
    }
    setFilteredData(filtered);
  }, [searchQuery, customerList, searchParams]);

  const handleCardClick = (type) => {
    setSelectedCustomerType(type);
    setCurrentPage(1);
    if (type === "all") {
      setFilteredData(customerList);
    } else if (type === "b2c") {
      const filtered = customerList.filter(
        (c) => c.customerType?.toLowerCase() === "individual",
      );
      setFilteredData(filtered);
    } else if (type === "b2b") {
      const filtered = customerList.filter(
        (c) => c.customerType?.toLowerCase() !== "individual",
      );
      setFilteredData(filtered);
    }
  };

  // Pagination Logic
  let currentItems = [];
  let totalPage = 1;
  let showingFrom = 0;
  let showingTo = 0;
  if (itemsPerPage === "all") {
    currentItems = filteredData;
    showingFrom = filteredData.length > 0 ? 1 : 0;
    showingTo = filteredData.length;
  } else {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
    totalPage = Math.ceil(filteredData.length / itemsPerPage);
    showingFrom = indexOfFirstItem + 1;
    showingTo = Math.min(indexOfLastItem, filteredData.length);
  }

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = currentItems.map((p) => p.id);
      setSelectedData((prev) => [...new Set([...prev, ...allIds])]);
    } else {
      const idsToRemove = currentItems.map((p) => p.id);
      setSelectedData((prev) => prev.filter((id) => !idsToRemove.includes(id)));
    }
  };

  const handleSelectOne = (id) => {
    setSelectedData((prev) =>
      prev.includes(id) ? prev.filter((pid) => pid !== id) : [...prev, id],
    );
  };

  const handleDownloadCSV = (exportScope = "current") => {
    let dataToExport = [];

    // ✅ PRIORITY 1: If items selected → ALWAYS export selected (overrides everything)
    if (selectedData.length > 0) {
      dataToExport = customerList.filter((p) => selectedData.includes(p.id));
    }
    // ✅ PRIORITY 2: If "Export All" clicked → export entire dataset
    else if (exportScope === "all") {
      dataToExport = customerList;
    }
    // ✅ PRIORITY 3: Default → export current page items only
    else {
      dataToExport = currentItems;
    }

    if (dataToExport.length === 0) {
      toast({ type: ERROR, message: "No customer data to download" });
      return;
    }

    const csvRows = dataToExport.map((customer, index) => ({
      "S.No": index + 1,
      "Cus ID": customer.id || "-",
      "Customer Name": customer.name || "-",
      "Customer Type": customer.customerType || "N/A",
      Email: customer.emailId || "N/A",
      PhoneNo: customer.phoneNumber || "N/A",
      Address:
        [customer.address1, customer.address2].filter(Boolean).join(", ") ||
        "N/A",
      "Total Transaction": customer.totalTransaction || "-",
    }));

    const columns = [
      "S.No",
      "Cus ID",
      "Customer Name",
      "Customer Type",
      "Email",
      "PhoneNo",
      "Address",
      "Total Transaction",
    ];

    const csv = Papa.unparse({
      fields: columns,
      csvRows,
    });

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;

    // ✅ Dynamic filename based on export type
    const fileName =
      selectedData.length > 0
        ? `Customer_Selected_${selectedData.length}.csv`
        : exportScope === "all"
          ? "Customer_All.csv"
          : `Customer_Page${currentPage}.csv`;

    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleView = (custId) => {
    navigate(`/admin/customer/customerDetails/${custId}`);
  };

  const handleDelete = (custId) => {
    if (window.confirm("Are you sure you want to delete this customer?")) {
      api
        .delete(`/customer/?id=${custId}`, adminReqConfig())
        .then((response) => {
          if (response) {
            toast({ type: SUCCESS, message: response.data.message });
            setCustomerList((prev) => prev.filter((p) => p.id !== custId));
            setFilteredData((prev) => prev.filter((p) => p.id !== custId));
          }
        })
        .catch((error) => {
          toast({ type: ERROR, message: "Failed to delete customer" });
          console.error("Delete error:", error);
        });
    }
  };
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isFilterPanelOpen && !e.target.closest(".nav-item")) {
        setIsFilterPanelOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isFilterPanelOpen]);

  const formatCustomerId = (customer) => {
    if (!customer?.createTimestamp || !customer?.id) return "CUS--";

    // Extract last 2 digits of year
    const year = new Date(customer.createTimestamp).getFullYear();
    const yy = String(year).slice(-2); // e.g., 2025 → "25"

    // Zero-pad order.id to 4 digits
    const paddedId = String(customer.id).padStart(4, "0"); // e.g., 7 → "0007"

    return `CUS${yy}-${paddedId}`;
  };

  return (
    <>
      <AdminSideBar
        menuContent={menuContent}
        isMinimized={isSidebarMinimized}
      />
      <AdminTopNavBar
        toggleSidebar={toggleSidebar}
        isSidebarMinimized={isSidebarMinimized}
        homeLink={homeLink}
        title="CUSTOMERS LIST"
      />
      <div
        className="content-wrapper p-0"
        style={{
          marginLeft: isSidebarMinimized ? "80px" : "250px",
          flex: "1",
        }}
      >
        <section
          style={{
            backgroundColor: "#F1F4F9",
            padding: "30px",
            marginTop: "70px",
            marginBottom: "40px",
            minHeight: "80vh",
          }}
        >
          <div className="summary-cards-section mb-24">
            <div className="row g-3">
              <div className="col-md-4">
                <div
                  className={` shadow-sm ${
                    selectedCustomerType === "all"
                      ? "selected-order-list-card"
                      : "order-list-card"
                  }`}
                  onClick={() => handleCardClick("all")}
                >
                  <div className="d-flex gap-10">
                    <div
                      className={`p-2 ${
                        selectedCustomerType === "all"
                          ? "selected-order-list-card-image"
                          : "order-list-card-image"
                      }`}
                    >
                      <img
                        src={
                          selectedCustomerType === "all"
                            ? "/assets/images/tot-cus.png"
                            : "/assets/images/tot-cus1.png"
                        }
                        style={{ height: "40px", width: "40px" }}
                      />
                    </div>
                    <div>
                      <p className="mb-1 order-list-amnt">All Customers</p>
                      <p className="mb-0 ">{customerList?.length} Customers</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div
                  className={` shadow-sm ${
                    selectedCustomerType === "b2b"
                      ? "selected-order-list-card"
                      : "order-list-card"
                  }`}
                  onClick={() => handleCardClick("b2b")}
                >
                  <div className="d-flex gap-10">
                    <div
                      className={`p-2 ${
                        selectedCustomerType === "b2b"
                          ? "selected-order-list-card-image"
                          : "order-list-card-image"
                      }`}
                    >
                      <img
                        src={
                          selectedCustomerType === "b2b"
                            ? "/assets/images/tot-b2b.png"
                            : "/assets/images/tot-b2b1.png"
                        }
                        style={{ height: "40px", width: "40px" }}
                      />
                    </div>
                    <div>
                      <p className="mb-1 order-list-amnt">B2B Customers</p>
                      <p className="mb-0 ">
                        {
                          customerList?.filter(
                            (c) =>
                              c.customerType?.toLowerCase() !== "individual",
                          ).length
                        }{" "}
                        Customers
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div
                  className={` shadow-sm ${
                    selectedCustomerType === "b2c"
                      ? "selected-order-list-card"
                      : "order-list-card"
                  }`}
                  onClick={() => handleCardClick("b2c")}
                >
                  <div className="d-flex gap-10">
                    <div
                      className={` p-2 ${
                        selectedCustomerType === "b2c"
                          ? "selected-order-list-card-image"
                          : "order-list-card-image"
                      }`}
                    >
                      <img
                        src={
                          selectedCustomerType === "b2c"
                            ? "/assets/images/tot-b2c.png"
                            : "/assets/images/tot-b2c1.png"
                        }
                        style={{ height: "40px", width: "40px" }}
                      />
                    </div>
                    <div>
                      <p className="mb-1 order-list-amnt">B2C Customers</p>
                      <p className="mb-0 ">
                        {
                          customerList?.filter(
                            (c) =>
                              c.customerType?.toLowerCase() === "individual",
                          ).length
                        }{" "}
                        Customers
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {fetchLoading ? (
            <p>Loading customers...</p>
          ) : (
            <>
              <div className="rounded-5 bg-white py-24 ">
                <div className="row align-items-center px-24 py-10 mb-3">
                  <div className="col-auto">
                    <h6 className="mb-0">All Customers List</h6>
                  </div>
                  <div className="col d-flex align-items-center">
                    <div className="nav-item" style={{ position: "relative" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          border: "1px solid #c6d6f5",
                          borderRadius: "8px",
                          backgroundColor: "#fff",
                          width: "500px",
                        }}
                      >
                        <img
                          src="/assets/images/search-icon.png"
                          style={{ width: "18px", marginLeft: "8px" }}
                        />
                        <input
                          type="text"
                          placeholder="Filter and search here..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          onFocus={() => setIsFilterPanelOpen(true)}
                          style={{
                            border: "none",
                            outline: "none",
                            padding: "8px 8px",
                            width: "100%",
                            fontSize: "14px",
                            color: "#333",
                            backgroundColor: "transparent",
                          }}
                        />
                        {searchQuery && (
                          <X
                            size={24}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSearchQuery("");
                              setCurrentPage(1);
                              setIsFilterPanelOpen(false);
                            }}
                            style={{
                              position: "absolute",
                              right: "40px",
                              cursor: "pointer",
                              color: "#999",
                              padding: "4px",
                              zIndex: 10,
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.color = "#666")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.color = "#999")
                            }
                          />
                        )}
                        <ChevronDown
                          size={16}
                          style={{
                            marginRight: "8px",
                            color: "#999",
                            cursor: "pointer",
                            transform: isFilterPanelOpen
                              ? "rotate(180deg)"
                              : "rotate(0deg)",
                            transition: "transform 0.2s",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsFilterPanelOpen(!isFilterPanelOpen);
                          }}
                        />
                      </div>
                      {isFilterPanelOpen && (
                        <FilterPanel
                          filterValues={filterValues}
                          onFilterChange={(key, value) =>
                            setFilterValues((prev) => ({
                              ...prev,
                              [key]: value,
                            }))
                          }
                          onApply={applyFilters}
                          onClear={clearFilters}
                        />
                      )}
                    </div>
                  </div>
                  <div className="d-flex col-auto">
                    <div className="">
                      <CustomDropdown handleDownloadCSV={handleDownloadCSV} />
                    </div>
                  </div>
                </div>

                {/* Table */}
                <div className="table-responsive mt-24 product-table">
                  <table className="table table-striped custom-striped-table-product-list align-middle">
                    <thead className="">
                      <tr>
                        <th scope="col" style={{ width: "50px" }}>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            style={{ borderColor: "#c5d5f5" }}
                            onChange={handleSelectAll}
                            checked={
                              currentItems.length > 0 &&
                              currentItems.every((p) =>
                                selectedData.includes(p.id),
                              )
                            }
                          />
                        </th>
                        <th
                          scope="col"
                          style={{ fontSize: "15px", textWrap: "nowrap" }}
                        >
                          Cust.ID
                        </th>
                        <th
                          scope="col"
                          style={{ fontSize: "15px", textWrap: "nowrap" }}
                        >
                          Customer Name
                        </th>
                        <th
                          scope="col"
                          style={{ fontSize: "15px", textWrap: "nowrap" }}
                        >
                          Customer Type
                        </th>
                        <th
                          scope="col"
                          style={{ fontSize: "15px", textWrap: "nowrap" }}
                        >
                          Phone Number
                        </th>
                        <th
                          scope="col"
                          style={{ fontSize: "15px", textWrap: "nowrap" }}
                        >
                          Address
                        </th>
                        <th
                          scope="col"
                          style={{ fontSize: "15px", textWrap: "nowrap" }}
                        >
                          Total Transaction
                        </th>
                        <th
                          scope="col"
                          style={{ fontSize: "15px", textWrap: "nowrap" }}
                        >
                          Date & Time
                        </th>
                        <th
                          scope="col"
                          style={{ fontSize: "15px", textWrap: "nowrap" }}
                        >
                          Status
                        </th>
                        <th
                          scope="col"
                          style={{ fontSize: "15px", textWrap: "nowrap" }}
                        >
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentItems.length > 0 ? (
                        currentItems.map((customer) => {
                          return (
                            <tr key={customer.id}>
                              <td>
                                <input
                                  type="checkbox"
                                  style={{
                                    borderColor: "#c5d5f5",
                                  }}
                                  className="form-check-input"
                                  checked={selectedData.includes(customer.id)}
                                  onChange={() => handleSelectOne(customer.id)}
                                />
                              </td>
                              <td
                                style={{
                                  textWrap: "nowrap",
                                  overflow: "hidden",
                                }}
                              >
                                {formatCustomerId(customer)}
                              </td>
                              <td>{customer.name || "N/A"}</td>
                              <td>{customer.customerType || "N/A"}</td>
                              <td>{customer.phoneNumber || "N/A"}</td>
                              <td>
                                {customer.address1 || "N/A"},{customer.address2}
                              </td>
                              <td>
                                ₹
                                {customer?.totalTransaction?.toFixed(2) ||
                                  "N/A"}
                              </td>
                              <td>
                                {formatShortMonthDateYearWithTime(
                                  customer.createTimestamp,
                                )}
                              </td>
                              <td>
                                <div className="form-check form-switch">
                                  <input
                                    className={`form-check-input form-product-status-switch ${
                                      customer.status === "active"
                                        ? "switch-green"
                                        : "switch-orange"
                                    }`}
                                    type="checkbox"
                                    id={`statusSwitch-${customer.id}`}
                                    checked={customer.status === "active"}
                                    onChange={() =>
                                      handleStatusChange(
                                        customer.id,
                                        customer.status,
                                      )
                                    }
                                  />
                                </div>
                              </td>
                              <td style={{ position: "relative" }}>
                                <div className="d-flex gap-1">
                                  {productPermissions.view && (
                                    <button
                                      style={{
                                        backgroundColor: "#d9ebff",
                                        height: "39px",
                                        width: "50px",
                                        border: "none",
                                        borderRadius: "4px",
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        cursor: "pointer",
                                        padding: "0",
                                        margin: "0",
                                        fontSize: "14px",
                                        lineHeight: 1,
                                        color: "inherit",
                                        outline: "none",
                                      }}
                                      onClick={() => handleView(customer.id)}
                                    >
                                      <img
                                        src="/assets/images/view-icon.png"
                                        alt="View"
                                        style={{
                                          width: "16px",
                                          height: "16px",
                                          objectFit: "contain",
                                        }}
                                        onError={(e) =>
                                          (e.target.style.display = "none")
                                        }
                                      />
                                    </button>
                                  )}
                                  {/* {productPermissions.delete && (
                                    <button
                                      style={{
                                        backgroundColor: "#d9ebff",
                                        height: "39px",
                                        width: "50px",
                                        border: "none",
                                        borderRadius: "4px",
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        cursor: "pointer",
                                        padding: "0",
                                        margin: "0",
                                        fontSize: "14px",
                                        lineHeight: 1,
                                        color: "inherit",
                                        outline: "none",
                                      }}
                                      onClick={() => handleDelete(customer.id)}
                                    >
                                      <img
                                        src="/assets/images/delete-icon.png"
                                        alt="Delete"
                                        style={{
                                          width: "16px",
                                          height: "16px",
                                          objectFit: "contain",
                                        }}
                                        onError={(e) =>
                                          (e.target.style.display = "none")
                                        }
                                      />
                                    </button>
                                  )} */}
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="9" className="text-center py-4">
                            No Data found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="d-flex justify-content-between align-items-center mt-16 p-16">
                  <div className="d-flex align-items-center">
                    <select
                      className="form-select d-inline-block w-auto"
                      value={itemsPerPage}
                      onChange={(e) => {
                        const val = e.target.value;
                        setItemsPerPage(val === "all" ? "all" : parseInt(val));
                        setCurrentPage(1);
                      }}
                      style={{
                        height: "34px",
                        fontSize: "14px",
                        minWidth: "70px",
                      }}
                    >
                      <option value="20">20</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                      <option value="all">All</option>
                    </select>
                    <span className="ms-2">
                      Showing {showingFrom} to {showingTo} of{" "}
                      {filteredData.length} Lists
                    </span>
                  </div>
                  <nav>
                    <ul className="pagination mt-0">
                      <li
                        className={`page-item ${
                          currentPage === 1 ? "disabled" : ""
                        }`}
                      >
                        <button
                          className="page-link"
                          onClick={() => paginate(currentPage - 1)}
                        >
                          &laquo;
                        </button>
                      </li>
                      {(() => {
                        const pagesToShow = 5;
                        let startPage = Math.max(
                          1,
                          currentPage - Math.floor(pagesToShow / 2),
                        );
                        let endPage = startPage + pagesToShow - 1;
                        if (endPage > totalPage) {
                          endPage = totalPage;
                          startPage = Math.max(1, endPage - pagesToShow + 1);
                        }
                        const pageNumbers = [];
                        for (let i = startPage; i <= endPage; i++) {
                          pageNumbers.push(i);
                        }
                        return pageNumbers.map((pageNum) => (
                          <li
                            key={pageNum}
                            className={`page-item ${
                              currentPage === pageNum ? "active" : ""
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => paginate(pageNum)}
                            >
                              {pageNum}
                            </button>
                          </li>
                        ));
                      })()}
                      <li
                        className={`page-item ${
                          currentPage === totalPage ? "disabled" : ""
                        }`}
                      >
                        <button
                          className="page-link"
                          onClick={() => paginate(currentPage + 1)}
                        >
                          &raquo;
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
            </>
          )}
          <AdminFooter isSidebarMinimized={isSidebarMinimized} />
        </section>
      </div>
    </>
  );
};

export default ListCustomer;

const CustomDropdown = ({ handleDownloadCSV }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState("Options");
  const [isHovered, setIsHovered] = useState(false);
  const dropdownRef = useRef(null);
  const options = [
    { label: "Export", value: "export" },
    { label: "Export All", value: "exportAll" },
  ];
  const handleSelect = (opt) => {
    setSelected(opt.label);
    setIsOpen(false);

    if (opt.value === "export") {
      handleDownloadCSV("current");
    } else if (opt.value === "exportAll") {
      handleDownloadCSV("all");
    }

    setSelected("Options");
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div
      className="custom-select"
      style={{ position: "relative", display: "inline-block" }}
      ref={dropdownRef}
    >
      <div
        className="select-header"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          backgroundColor: isHovered ? "#EDF8FA" : "#fff",
          color: "rgba(129, 134, 134, 1)",
          padding: "8px 12px",
          border: "1px solid #ccc",
          borderRadius: "4px",
          cursor: "pointer",
          width: "140px",
          height: "40px",
          textAlign: "left",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: 500,
          transition: "background-color 0.2s ease",
        }}
      >
        <span>{selected}</span>
        <ChevronDown />
      </div>
      {isOpen && (
        <ul
          className="select-options"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            width: "100%",
            listStyle: "none",
            margin: 0,
            padding: 0,
            backgroundColor: "white",
            border: "none",
            borderRadius: "4px",
            zIndex: 1000,
            boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
            fontWeight: 500,
          }}
        >
          {options.map((opt) => (
            <li
              key={opt.value}
              onClick={() => handleSelect(opt)}
              className="select-option"
              style={{
                padding: "8px 12px",
                color: "rgba(129, 134, 134, 1)",
                backgroundColor: "white",
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ✅ Simplified FilterPanel: ONLY Customer Type
const FilterPanel = ({ filterValues, onFilterChange, onApply, onClear }) => {
  return (
    <div
      style={{
        position: "absolute",
        top: "100%",
        left: 0,
        width: "500px",
        backgroundColor: "#fff",
        borderRadius: "8px",
        boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
        zIndex: 1000,
        padding: "16px",
        maxHeight: "200px",
        overflowY: "auto",
        marginTop: "10px",
      }}
    >
      <div className="row g-3">
        <div className="col-12">
          <select
            className="form-select filter-input"
            value={filterValues.customerType}
            onChange={(e) => onFilterChange("customerType", e.target.value)}
          >
            <option value="">All Customer Types</option>
            <option value="dealer">Dealer</option>
            <option value="sub-dealer">Sub Dealer</option>
            <option value="individual">Individual</option>
            <option value="engineer">Engineer</option>
            <option value="builder">Builder</option>
            <option value="architect">Architect</option>
            <option value="contractor">Contractor</option>
          </select>
        </div>
      </div>
      <div className="d-flex gap-14 mt-24">
        <button
          onClick={onApply}
          style={{
            color: "#fff",
            backgroundColor: "#2b95d3",
            borderRadius: "6px",
            fontSize: "14px",
            fontWeight: "700",
            cursor: "pointer",
            padding: "8px 16px",
            minWidth: "150px",
            height: "40px",
            border: "none",
          }}
        >
          Apply Filter
        </button>
        <button
          onClick={onClear}
          style={{
            color: "#2692d2",
            backgroundColor: "#d9ebff",
            borderRadius: "6px",
            fontSize: "14px",
            fontWeight: "700",
            cursor: "pointer",
            padding: "8px 16px",
            minWidth: "150px",
            height: "40px",
            border: "none",
            gap: "6px",
          }}
        >
          Clear All
        </button>
      </div>
    </div>
  );
};
